import { Injectable, Inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  IChatbotHistoryApiService,
  LazyLoadQueryParams,
  SessionHistoryDTO,
  SessionDTO,
  SortDirection
} from './chatbot-history-api.service';

const DEFAULT_PAGE_SIZE = 500;

/**
 * Service for chatbot history operations.
 * Uses the API service to load sessions and maps them to grouped chat history.
 */
@Injectable()
export class ChatbotHistoryService {
  constructor(
    @Inject('ChatbotHistoryApiService') private _api: IChatbotHistoryApiService
  ) { }

  private _firstMessageSent$ = new Subject<void>();
  public firstMessageSent$: Observable<void> = this._firstMessageSent$.asObservable();

  /**
   * Retrieves the chat history groups organized by date (first page only).
   * @returns An observable emitting an array of ChatHistoryGroup objects.
   */
  public getSessions(searchText: string): Observable<ChatHistoryGroup[]> {
    const params: LazyLoadQueryParams = {
      includeCount: false,
      toSkip: 0,
      rowsCount: DEFAULT_PAGE_SIZE,
      sortField: 'date',
      sortDirection: SortDirection.Descending,
      filter: null,
    };
    return this._api.getSessions(searchText, params).pipe(
      map((page) => this.mapSessionsToGroups(page.rows))
    );
  }

  /**
   * Loads a session by id (includes messages and session metadata).
   * @param sessionId - The session id (numeric).
   * @returns An observable emitting the SessionDTO.
   */
  public getSession(sessionId: number): Observable<SessionDTO> {
    return this._api.getSession(sessionId);
  }

  /** Maps API session rows into UI groups (by date) and items. Exposed for components that need to map raw API pages (e.g. pagination). */
  public mapSessionsToGroups(sessions: SessionHistoryDTO[]): ChatHistoryGroup[] {
    const groupByDate = new Map<string, ChatHistoryItem[]>();
    for (const session of sessions) {
      // date is in local timezone
      const date = session.date;
      const dateKey = date.toDateString();
      const item: ChatHistoryItem = {
        llmSessionId: session.llmSessionId,
        sessionId: session.sessionId,
        title: session.sessionTitle,
        date,
        isActive: false,
      };

      // add session to date list
      const list = groupByDate.get(dateKey) ?? [];
      list.push(item);
      groupByDate.set(dateKey, list);
    }

    const groups: ChatHistoryGroup[] = [];
    const sortedKeys = Array.from(groupByDate.keys()).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );
    for (const key of sortedKeys) {
      const date = new Date(key);
      groups.push({
        date,
        items: groupByDate.get(key)!,
        isExpanded: false,
      });
    }

    // expand 1st group only
    if (groups.length > 0) {
      groups[0].isExpanded = true;
    }

    return groups;
  }

  /**
   * Notifies that the first message has been sent in a session, which can be used to trigger history refreshes in subscribed components.
   */
  public notifyFirstMessageSent(): void {
    this._firstMessageSent$.next();
  }
}

/**
 * Represents a single chat history item (session) in the UI
 */
export interface ChatHistoryItem {
  sessionId: number;
  llmSessionId: string;
  title: string;
  date: Date;
  isActive: boolean;
}

/**
 * Represents a group of chat history items by date
 */
export interface ChatHistoryGroup {
  date: Date;
  items: ChatHistoryItem[];
  isExpanded: boolean;
}

export interface ChatSessionChangedEvent {
  action: 'session-changed';
  sessionId: number;
  timestamp: number;
}
